import type { App } from "vue";
import type { Router } from "vue-router";

import { isAppError } from "@/shared/api";
import { logger } from "@/shared/lib/logger";

/**
 * Глобальні пастки спостереження (ADR-0011 D3, twin of `installAuthShell`).
 * Збирає всі помилки `web/` в logger-порт: uncaught Vue render/lifecycle,
 * uncaught JS, rejected promises. **Log-only** (рішення власника № 3): візуального
 * `<ErrorBoundary>` ця ітерація НЕ створює.
 *
 * FSD: `app/` (вершина) → вниз у `shared` (logger, isAppError) — без порушень.
 * `router?` — заділ під route-context enrichment (path уже читається з
 * `window.location`); user-context (`user_id`/`salon_id`) — до remote-часу.
 *
 * Порядок (main.ts, §9): викликати якомога раніше в `bootstrap()` — одразу після
 * `createApp`, щоб window-пастки ловили boot-time помилки решти setup-у.
 */
export function installObservability(params: { app: App; router?: Router }): void {
  const { app } = params;

  app.config.errorHandler = (err, _instance, info) => {
    logger.error("vue-error", { source: "vue", error: err, info });
  };

  window.addEventListener("error", (e) => {
    logger.error("window-error", {
      source: "window",
      error: e.error,
      message: e.message,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason: unknown = e.reason;
    if (isAppError(reason)) {
      // Уже залоговано в interceptor (§6) — не дублюємо як error. Опційний debug —
      // сигнал «call-site забув catch» (дедуплікація, ADR-0011 D4).
      logger.debug("unhandled-apperror", {
        source: "unhandledrejection",
        kind: reason.kind,
      });
      return;
    }
    logger.error("unhandled-rejection", {
      source: "unhandledrejection",
      error: reason,
    });
  });
}
